import { useEffect, useRef, useState } from 'react'
import { Activity, CheckCircle2, Eye, RotateCw, XCircle } from 'lucide-react'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '#app/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '#app/components/ui/table'
import { cn } from '#app/utils/misc'

type DemoStatus = 'delivered' | 'failed' | 'pending' | 'retrying'

type DemoRow = {
  key: string
  ageSec: number
  fromEmail: string
  subject: string
  status: DemoStatus
  responseCode: number | null
  durationMs: number | null
  attempt: number
  maxAttempts: number
  fresh: boolean
}

type SampleRow = Omit<DemoRow, 'key' | 'ageSec' | 'fresh'>

const SAMPLE: SampleRow[] = [
  { fromEmail: 'stripe@stripe.com', subject: 'Payment received for invoice INV-4829', status: 'delivered', responseCode: 200, durationMs: 87, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'orders@shopify.com', subject: 'Order #10234 placed — $84.50', status: 'delivered', responseCode: 200, durationMs: 124, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'no-reply@github.com', subject: 'PR #142 review requested', status: 'delivered', responseCode: 202, durationMs: 203, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'notifications@linear.app', subject: 'ENG-589 moved to In Review', status: 'delivered', responseCode: 200, durationMs: 156, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'alerts@sentry.io', subject: 'New issue: DatabaseTimeoutError', status: 'retrying', responseCode: 503, durationMs: 1240, attempt: 2, maxAttempts: 5 },
  { fromEmail: 'billing@aws.amazon.com', subject: 'AWS bill ready — $1,249.18', status: 'delivered', responseCode: 200, durationMs: 98, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'hello@calendly.com', subject: 'Meeting confirmed: Product sync 3pm', status: 'delivered', responseCode: 200, durationMs: 142, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'security@1password.com', subject: 'Sign-in from new device', status: 'delivered', responseCode: 200, durationMs: 74, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'team@vercel.com', subject: 'Deployment ready: prod-xkz2', status: 'delivered', responseCode: 200, durationMs: 189, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'noreply@twilio.com', subject: 'SMS usage alert: 85% of limit', status: 'failed', responseCode: 401, durationMs: 68, attempt: 5, maxAttempts: 5 },
  { fromEmail: 'updates@postmarkapp.com', subject: 'Monthly delivery report', status: 'delivered', responseCode: 200, durationMs: 156, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'no-reply@notion.so', subject: 'Ryan shared "Q2 Roadmap"', status: 'delivered', responseCode: 200, durationMs: 211, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'receipts@square.com', subject: 'Receipt from Ritual Coffee — $6.25', status: 'delivered', responseCode: 200, durationMs: 93, attempt: 1, maxAttempts: 5 },
  { fromEmail: 'support@zendesk.com', subject: 'Ticket #8821 updated', status: 'pending', responseCode: null, durationMs: null, attempt: 1, maxAttempts: 5 },
]

const MAX_ROWS = 7
const TICK_MS = 2500

const INITIAL_STATS = { delivered: 1247, retrying: 12, failed: 2 }

function sampleAt(seq: number): SampleRow {
  return SAMPLE[seq % SAMPLE.length]
}

const INITIAL_AGES = [3, 14, 29, 52, 81, 118, 164]
const INITIAL_ROWS: DemoRow[] = INITIAL_AGES.map((age, i) => ({
  key: `init-${i}`,
  ageSec: age,
  fresh: false,
  ...sampleAt(i),
}))

function formatAge(sec: number): string {
  if (sec < 2) return 'just now'
  if (sec < 60) return `${Math.floor(sec)}s ago`
  const min = Math.floor(sec / 60)
  return `${min}m ago`
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

const STATUS_STYLE: Record<DemoStatus, string> = {
  delivered: 'bg-green-500/15 text-green-700 dark:text-green-400',
  failed: 'bg-red-500/15 text-red-700 dark:text-red-400',
  pending: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-400',
  retrying: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
}

function statusLabel(status: DemoStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function codeClass(code: number | null): string {
  if (code == null) return 'text-muted-foreground'
  if (code >= 200 && code < 300) return 'text-green-700 dark:text-green-400'
  if (code >= 400) return 'text-red-700 dark:text-red-400'
  return 'text-muted-foreground'
}

function computeSuccessRate({ delivered, retrying, failed }: typeof INITIAL_STATS): string {
  const total = delivered + retrying + failed
  if (total === 0) return '—'
  const pct = (delivered / total) * 100
  return `${pct.toFixed(1)}%`
}

export function DemoDeliveryTable() {
  const [rows, setRows] = useState<DemoRow[]>(INITIAL_ROWS)
  const [stats, setStats] = useState(INITIAL_STATS)
  const containerRef = useRef<HTMLDivElement>(null)
  const seqRef = useRef<number>(INITIAL_ROWS.length)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let intervalId: number | null = null
    let inView = false

    const tick = () => {
      seqRef.current += 1
      const seq = seqRef.current
      const sample = sampleAt(seq)
      const newRow: DemoRow = {
        key: `live-${seq}`,
        ageSec: 0,
        fresh: true,
        ...sample,
      }
      setRows((prev) => {
        const bumped = prev.map((r) => ({
          ...r,
          ageSec: r.ageSec + TICK_MS / 1000,
          fresh: false,
        }))
        return [newRow, ...bumped].slice(0, MAX_ROWS)
      })
      setStats((prev) => {
        switch (sample.status) {
          case 'delivered':
            return { ...prev, delivered: prev.delivered + 1 }
          case 'retrying':
            return { ...prev, retrying: prev.retrying + 1 }
          case 'failed':
            return { ...prev, failed: prev.failed + 1 }
          default:
            return prev
        }
      })
      window.setTimeout(() => {
        setRows((prev) =>
          prev.map((r) => (r.key === newRow.key ? { ...r, fresh: false } : r)),
        )
      }, 600)
    }

    const start = () => {
      if (intervalId != null) return
      intervalId = window.setInterval(tick, TICK_MS)
    }
    const stop = () => {
      if (intervalId != null) {
        window.clearInterval(intervalId)
        intervalId = null
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting
        if (inView && !document.hidden) start()
        else stop()
      },
      { threshold: 0.1 },
    )
    if (containerRef.current) observer.observe(containerRef.current)

    const onVisibility = () => {
      if (document.hidden) stop()
      else if (inView) start()
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      stop()
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return (
    <div ref={containerRef} className="w-full" aria-hidden="true">
      <Card className="overflow-hidden shadow-lg">
        <CardHeader className="space-y-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Delivery History</CardTitle>
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
              Live
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
              <span className="font-semibold tabular-nums text-primary">
                {stats.delivered.toLocaleString()}
              </span>
              <span className="text-muted-foreground">delivered</span>
            </span>
            <span className="flex items-center gap-1.5">
              <RotateCw className="h-3.5 w-3.5 text-blue-500" />
              <span className="font-semibold tabular-nums text-primary">{stats.retrying}</span>
              <span className="text-muted-foreground">retrying</span>
            </span>
            <span className="flex items-center gap-1.5">
              <XCircle className="h-3.5 w-3.5 text-red-500" />
              <span className="font-semibold tabular-nums text-primary">{stats.failed}</span>
              <span className="text-muted-foreground">failed</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-blue-500" />
              <span className="font-semibold tabular-nums text-primary">
                {computeSuccessRate(stats)}
              </span>
              <span className="text-muted-foreground">success</span>
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div
            className="relative"
            style={{
              maskImage: 'linear-gradient(to bottom, black 65%, transparent)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 65%, transparent)',
            }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">Received</TableHead>
                  <TableHead className="w-[200px]">From</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead className="w-[80px]">Response</TableHead>
                  <TableHead className="w-[80px]">Duration</TableHead>
                  <TableHead className="w-[80px]">Attempts</TableHead>
                  <TableHead className="w-[90px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.key}
                    className={cn(
                      'transition-colors duration-500',
                      r.fresh && 'bg-primary/5',
                    )}>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatAge(r.ageSec)}
                    </TableCell>
                    <TableCell className="truncate font-mono text-xs">
                      {r.fromEmail}
                    </TableCell>
                    <TableCell className="max-w-[300px] truncate text-sm">
                      {r.subject}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={STATUS_STYLE[r.status]}>
                        {statusLabel(r.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className={cn('font-mono text-xs', codeClass(r.responseCode))}>
                      {r.responseCode ?? '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {formatDuration(r.durationMs)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.attempt}/{r.maxAttempts}
                    </TableCell>
                    <TableCell>
                      <div className="pointer-events-none flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <RotateCw className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <Eye className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
